import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

export default function InvoicePrint() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${invoiceId}`).then((r) => setData(r.data));
  }, [invoiceId]);

  if (!data) return <div className="p-6 text-[#6A6E85]">Loading…</div>;
  const { invoice, client, job, site } = data;

  return (
    <div className="min-h-screen bg-[#F5F6FA] py-6">
      <div className="max-w-3xl mx-auto px-4">
        <div className="no-print flex items-center justify-between mb-4">
          <Link
            to="/office/invoices"
            className="inline-flex items-center gap-2 text-sm text-[#181854] hover:underline"
          >
            <ArrowLeft size={14} /> Back to invoices
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="invoice-print-btn"
            className="inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
          >
            <Printer size={16} /> Print / Save PDF
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E4E6EF] p-8 shadow-sm print:shadow-none print:border-0">
          <header className="flex items-start justify-between gap-6 pb-6 border-b border-[#E4E6EF]">
            <div className="flex items-center gap-4">
              <img
                src="/brand/gt-logo.webp"
                alt="GT Refrigeration & Aircon Services"
                className="h-12 w-auto object-contain"
                style={{ maxWidth: "240px" }}
              />
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-[#6A6E85] font-semibold">Tax Invoice</p>
              <h1 className="text-2xl font-bold text-[#181854] tracking-tight" data-testid="print-inv-number">
                {invoice.number || "—"}
              </h1>
              <p className="text-sm text-[#6A6E85] mt-1">Issued {fmt(invoice.created_at)}</p>
              <p className="text-xs uppercase tracking-widest font-semibold mt-2 inline-block px-2 py-0.5 rounded-full bg-[#ECEDF6] text-[#181854]">
                {invoice.status}
              </p>
            </div>
          </header>

          <section className="grid sm:grid-cols-2 gap-6 py-6 border-b border-[#E4E6EF]">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#6A6E85] font-semibold">Billed to</p>
              <p className="mt-1 font-semibold text-[#14162E]">{client?.name}</p>
              {client?.emails?.[0] && <p className="text-sm text-[#6A6E85]">{client.emails[0]}</p>}
              {client?.phones?.[0] && <p className="text-sm text-[#6A6E85]">{client.phones[0]}</p>}
              {client?.billing_ref && <p className="text-sm text-[#6A6E85] mt-1">Ref: {client.billing_ref}</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#6A6E85] font-semibold">Job</p>
              <p className="mt-1 font-semibold text-[#14162E]">{job?.title}</p>
              {site?.address && <p className="text-sm text-[#6A6E85]">{site.address}</p>}
            </div>
          </section>

          <section className="py-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#6A6E85]">
                  <th className="pb-3 font-semibold">Description</th>
                  <th className="pb-3 font-semibold text-right">Qty</th>
                  <th className="pb-3 font-semibold text-right">Unit price</th>
                  <th className="pb-3 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4E6EF]">
                {invoice.line_items.map((li, i) => (
                  <tr key={i}>
                    <td className="py-3 text-[#14162E]">{li.description}</td>
                    <td className="py-3 text-right text-[#14162E]">{li.qty}</td>
                    <td className="py-3 text-right text-[#14162E]">${li.unit_price.toFixed(2)}</td>
                    <td className="py-3 text-right text-[#14162E]">${li.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="pt-4 text-right font-semibold text-[#181854] uppercase text-xs tracking-widest">Total (AUD)</td>
                  <td className="pt-4 text-right font-bold text-xl text-[#14162E]">${invoice.amount.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </section>

          <footer className="pt-6 border-t border-[#E4E6EF] text-xs text-[#6A6E85] text-center">
            GT Refrigeration &amp; Aircon Services · Commercial Cooling. Performance Proven.
          </footer>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>
    </div>
  );
}
